import type { TagProps } from '@chakra-ui/react';
import React from 'react';

import type { StatsInterval, StatsIntervalIds } from 'types/client/stats';
import type { SelectOption } from 'ui/shared/select/types';

import { useI18n } from 'lib/i18n/context';
import Skeleton from 'ui/shared/chakra/Skeleton';
import Select from 'ui/shared/select/Select';
import TagGroupSelect from 'ui/shared/tagGroupSelect/TagGroupSelect';
import { STATS_INTERVAL_KEYS } from 'ui/stats/i18n';

type Props = {
  interval: StatsIntervalIds;
  onIntervalChange: (newInterval: StatsIntervalIds) => void;
  isLoading?: boolean;
  selectTagSize?: TagProps['size'];
};

const ChartIntervalSelect = ({ interval, onIntervalChange, isLoading, selectTagSize }: Props) => {
  const { t } = useI18n();
  const intervalList = React.useMemo(() => Object.keys(STATS_INTERVAL_KEYS).map((id: string) => ({
    value: id,
    label: t(STATS_INTERVAL_KEYS[id as StatsIntervalIds].title),
  })) as Array<SelectOption>, [ t ]);

  const intervalListShort = React.useMemo(() => Object.keys(STATS_INTERVAL_KEYS).map((id: string) => ({
    id: id,
    title: t(STATS_INTERVAL_KEYS[id as StatsIntervalIds].shortTitle),
  })) as Array<StatsInterval>, [ t ]);

  return (
    <>
      <Skeleton display={{ base: 'none', lg: 'flex' }} borderRadius="base" isLoaded={ !isLoading }>
        <TagGroupSelect<StatsIntervalIds> items={ intervalListShort } onChange={ onIntervalChange } value={ interval } tagSize={ selectTagSize }/>
      </Skeleton>
      <Select
        options={ intervalList }
        defaultValue={ interval }
        onChange={ onIntervalChange }
        isLoading={ isLoading }
        w={{ base: '100%', lg: '136px' }}
        display={{ base: 'flex', lg: 'none' }}
        flexShrink={ 0 }
        fontWeight={ 600 }
      />
    </>
  );
};

export default React.memo(ChartIntervalSelect);
